import React from 'react';
import styled, { keyframes } from 'styled-components';

// Animation keyframes
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
`;

const SponsorsSection = styled.section`
  padding: 6rem 0;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23990011' fill-opacity='0.03'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E") repeat;
    z-index: 1;
  }
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
  position: relative;
  z-index: 2;
`;

const MainTitle = styled.h2`
  font-size: 3rem;
  font-weight: 800;
  text-align: center;
  margin-bottom: 1rem;
  background: linear-gradient(45deg, #990011, #a48d26);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: ${fadeInUp} 0.8s ease-out;

  @media (max-width: 768px) {
    font-size: 2.2rem;
  }
`;

const Subtitle = styled.p`
  text-align: center;
  font-size: 1.2rem;
  color: #6c757d;
  margin-bottom: 4rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  animation: ${fadeInUp} 0.8s ease-out 0.2s both;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const SectionGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  margin-bottom: 5rem;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 3rem;
  }
`;

const SectionCard = styled.div`
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 3rem;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  position: relative;
  overflow: hidden;
  animation: ${fadeInUp} 0.8s ease-out 0.4s both;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 25px 50px rgba(0, 0, 0, 0.15);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 2px;
    background: linear-gradient(90deg, transparent, #990011, transparent);
    animation: ${shimmer} 2s infinite;
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h3`
  font-size: 1.8rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  color: #333;
`;

const SectionDescription = styled.p`
  color: #6c757d;
  font-style: italic;
  line-height: 1.6;
`;

const LogoContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 120px;
  position: relative;
`;

const Logo = styled.img.attrs(() => ({ loading: 'lazy' }))`
  max-height: 110px;
  max-width: 200px;
  width: auto;
  height: auto;
  filter: grayscale(30%) brightness(1.1);
  transition: all 0.4s ease;
  animation: ${float} 5s ease-in-out infinite;

  &:hover {
    filter: grayscale(0%) brightness(1.2);
    transform: scale(1.1);
  }
`;

const CollaborationsSection = styled.div`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(15px);
  border-radius: 25px;
  padding: 4rem 3rem;
  box-shadow: 0 30px 60px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.3);
  animation: ${fadeInUp} 0.8s ease-out 0.6s both;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 3px;
    background: linear-gradient(90deg, transparent, #a48d26, transparent);
    animation: ${shimmer} 3s infinite;
  }
`;

const CollaborationsTitle = styled.h3`
  font-size: 2.2rem;
  font-weight: 700;
  text-align: center;
  margin-bottom: 3rem;
  background: linear-gradient(45deg, #a48d26, #990011);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (max-width: 768px) {
    font-size: 1.8rem;
  }
`;

const CollaborationsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 2rem;
  align-items: center;
  justify-items: center;
`;

const CollaborationItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1.5rem;
  border-radius: 15px;
  background: rgba(255, 255, 255, 0.5);
  transition: all 0.3s ease;
  min-height: 140px;
  justify-content: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(153, 0, 17, 0.05) 0%, rgba(164, 141, 38, 0.05) 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    transform: translateY(-8px) scale(1.05);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.15);
    
    &::before {
      opacity: 1;
    }
  }
`;

const CollaborationLogo = styled.img.attrs(() => ({ loading: 'lazy' }))`
  max-height: 50px;
  max-width: 120px;
  width: auto;
  height: auto;
  margin-bottom: 1rem;
  filter: grayscale(40%) brightness(1.1);
  transition: all 0.3s ease;

  ${CollaborationItem}:hover & {
    filter: grayscale(0%) brightness(1.2);
    transform: scale(1.1);
  }
`;

const CollaborationName = styled.span`
  font-size: 0.9rem;
  font-weight: 600;
  color: #555;
  text-align: center;
`;

const DecorativeElement = styled.div`
  position: absolute;
  width: 100px;
  height: 100px;
  background: linear-gradient(45deg, rgba(153, 0, 17, 0.1), rgba(164, 141, 38, 0.1));
  border-radius: 50%;
  top: -50px;
  right: -50px;
  animation: ${float} 4s ease-in-out infinite reverse;
`;

function EnhancedSponsorsSection() {
  // Sample data - replace with your actual data
  const actionSponsors = [
    {
      name: 'Sidi Ali',
      logo: `${process.env.PUBLIC_URL}/images/logo-sponsor-collab/sidi-ali-oulmes.webp`,
      description: 'Generously supported our actions in the past.'
    },
    {
      name: 'BIC',
      logo: `${process.env.PUBLIC_URL}/images/logo-sponsor-collab/Bic-Logo.webp`,
      description: 'Generously supported our actions in the past.'
    },
    {
      name: 'Foods & Goods',
      logo: `${process.env.PUBLIC_URL}/images/logo-sponsor-collab/fg-logo.webp`,
      description: 'Generously supported our actions in the past.'
    },
    {
      name: 'Laprophan',
      logo: `${process.env.PUBLIC_URL}/images/logo-sponsor-collab/laprophan-logo.webp`,
      description: 'Generously gave us meds in this action.'
    }
  ];

  const strategicPartners = [
    {
      name: 'Intelcia',
      logo: `${process.env.PUBLIC_URL}/images/logo-sponsor-collab/intelcia-logo-final.jpg`,
      description: 'Active partner contributing with donations and in-kind support.'
    }
  ];

  const internationalPartners = [
    {
      name: 'APIP',
      logo: `${process.env.PUBLIC_URL}/images/logo-sponsor-collab/apip.png`
    }
  ];

  const collaborations = [
    { name: 'IFIAG', logo: `${process.env.PUBLIC_URL}/images/logo-ifiag-site2.png` },
    { name: 'Enactus', logo: `${process.env.PUBLIC_URL}/images/logo-sponsor-collab/Enactus_Full_Color_logo.png` },
    { name: 'Tawasol', logo: `${process.env.PUBLIC_URL}/images/logo-sponsor-collab/tawasol-logo.png` },
    { name: 'Diriddik', logo: `${process.env.PUBLIC_URL}/images/diriddik-removebg-preview.png` },
    // { name: 'TBH', logo: `${process.env.PUBLIC_URL}/images/Tbh-logo-3d.png` }
  ];

  return (
    <SponsorsSection>
      <Container>
        {/* <MainTitle>Organizations that have trusted us</MainTitle>
        <Subtitle>
          Partners and sponsors who placed their trust in us
        </Subtitle> */}

        {/* <SectionGrid>
          <SectionCard>
            <DecorativeElement />
            <SectionHeader>
              <SectionTitle>Partners</SectionTitle>
              <SectionDescription>
                Partners who contribute with donations or in-kind support
              </SectionDescription>
            </SectionHeader>
            <LogoContainer>
              {strategicPartners.map((partner, index) => (
                <Logo
                  key={index}
                  src={partner.logo}
                  alt={partner.name}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              ))}
            </LogoContainer>
          </SectionCard>

          <SectionCard>
            <DecorativeElement />
            <SectionHeader>
              <SectionTitle>Sponsors</SectionTitle>
              <SectionDescription>
                Occasional or action-based sponsors
              </SectionDescription>
            </SectionHeader>
            <LogoContainer>
              {actionSponsors.map((sponsor, index) => (
                <Logo
                  key={index}
                  src={sponsor.logo}
                  alt={sponsor.name}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              ))}
            </LogoContainer>
          </SectionCard>
        </SectionGrid> */}

        <CollaborationsSection style={{ marginBottom: '5rem' }}>
          <DecorativeElement style={{ top: '-50px', left: '-50px' }} />
          <CollaborationsTitle>Organizations that have trusted us</CollaborationsTitle>
          <CollaborationsGrid>
            {actionSponsors.map((sponsor, index) => (
              <CollaborationItem key={index} style={{ minHeight: '220px', padding: '2rem' }}>
                <CollaborationLogo
                  src={sponsor.logo}
                  alt={sponsor.name}
                  style={{ maxHeight: '120px', maxWidth: '250px', marginBottom: '1.5rem' }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
                <CollaborationName style={{ fontSize: '1.3rem' }}>{sponsor.name}</CollaborationName>
              </CollaborationItem>
            ))}
          </CollaborationsGrid>
        </CollaborationsSection>


        <CollaborationsSection style={{ marginBottom: '5rem' }}>
          <DecorativeElement style={{ top: '-50px', left: '-50px' }} />
          <CollaborationsTitle>International Institutional Partners</CollaborationsTitle>
          <CollaborationsGrid>
            {internationalPartners.map((partner, index) => (
              <CollaborationItem key={index} style={{ minHeight: '220px', padding: '2rem' }}>
                <CollaborationLogo
                  src={partner.logo}
                  alt={partner.name}
                  style={{ maxHeight: '120px', maxWidth: '250px', marginBottom: '1.5rem' }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
                <CollaborationName style={{ fontSize: '1.3rem' }}>{partner.name}</CollaborationName>
              </CollaborationItem>
            ))}
          </CollaborationsGrid>
        </CollaborationsSection>

        {/* <CollaborationsSection>
          <DecorativeElement style={{ top: 'auto', bottom: '-50px', left: '-50px' }} />
          <CollaborationsTitle>Supporting Companies</CollaborationsTitle>
          <CollaborationsGrid>
            {collaborations.map((collab, index) => (
              <CollaborationItem key={index}>
                <CollaborationLogo
                  src={collab.logo}
                  alt={collab.name}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
                <CollaborationName>{collab.name}</CollaborationName>
              </CollaborationItem>
            ))}
          </CollaborationsGrid>
        </CollaborationsSection> */}
      </Container>
    </SponsorsSection>
  );
}

export default EnhancedSponsorsSection;